import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Fuel, User, Route as RouteIcon, Receipt,
  Shield, Building2, Wrench, Package, Clock, TrendingUp, X, AlertTriangle, CheckCircle2, CalendarCheck, Users,
} from "lucide-react";
import { apiBaseUrl, authHeaders, isAdmin } from "./shared";

const DATE_PRESETS = [
  { key: "all", label: "All time" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_30", label: "Last 30 days" },
];

const STATUS_COLOR = {
  queued: "#7c3aed",       // inbound from the TMS, not yet picked up by a dispatcher
  available: "#5b9bd5",
  assigned: "#f2a33d",
  in_transit: "#1d6fd8",
  delivered: "#4cae6d",
  cancelled: "#9aa7b4",
};
function statusColor(status) {
  return STATUS_COLOR[status] || "#9aa7b4";
}
function statusLabel(status) {
  if (!status) return "Unknown";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Colored initials avatar for driver rows -- deterministic color per name so a
// driver keeps the same badge across sessions.
const AVATAR_COLORS = ["#3b82f6", "#8b5cf6", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#6366f1", "#f97316"];
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const s = (parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
  return s.toUpperCase() || "?";
}
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function fmtMoney(n) {
  return n == null ? "—" : `${n < 0 ? "-" : ""}$${Math.round(Math.abs(n)).toLocaleString()}`;
}
function fmtRate(n) {
  return n == null ? "—" : `$${n.toFixed(2)}/mi`;
}
function fmtMiles(n) {
  return n == null ? "—" : `${Math.round(n).toLocaleString()} mi`;
}
function fmtHours(n) {
  if (n == null) return "—";
  const totalMin = Math.round(n * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
function fmtDate(isoLike) {
  if (!isoLike) return "—";
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function CostLine({ icon: Icon, label, value, caption, accent, warn }) {
  return (
    <div style={styles.costLine}>
      <div style={styles.costLineIcon}><Icon size={13} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.costLineLabel}>{label}</div>
        {caption && <div style={styles.costLineCaption}>{caption}</div>}
      </div>
      <div style={{ ...styles.costLineValue, ...(accent ? { color: "var(--brand)" } : {}), ...(warn ? { color: "#e0745d" } : {}) }}>
        {value}
      </div>
    </div>
  );
}

// Only meaningful for loads that actually had a truck moving -- the
// backend only computes these for in_transit/delivered loads (see
// loads_with_economics), so off_route_events stays null (not 0) for
// anything else, and we distinguish "never checked" from "checked, clean."
function RouteFidelity({ l }) {
  const checked = l.off_route_events != null;
  if (!checked) return null;
  const wentOffRoute = l.off_route_events > 0;
  const tollOver = l.toll_overage != null && l.toll_overage > 1; // ignore cent-level noise
  if (!wentOffRoute && !tollOver) {
    return (
      <CostLine
        icon={CheckCircle2}
        label="Route fidelity"
        value="On planned route"
        accent
      />
    );
  }
  return (
    <>
      {wentOffRoute && (
        <CostLine
          icon={AlertTriangle}
          label="Went off planned route"
          value={`${l.off_route_events} event${l.off_route_events === 1 ? "" : "s"}`}
          caption={l.max_distance_off_route_miles ? `Up to ${fmtMiles(l.max_distance_off_route_miles)} off course` : null}
          warn
        />
      )}
      {l.actual_toll_total != null && (
        <CostLine
          icon={Receipt}
          label="Actual tolls paid"
          value={fmtMoney(l.actual_toll_total)}
          caption={tollOver ? `$${Math.round(l.toll_overage).toLocaleString()} over the planned route's toll estimate` : null}
          warn={tollOver}
        />
      )}
    </>
  );
}

function LoadDetail({ l }) {
  if (!l.has_route) {
    return (
      <div style={styles.detailEmpty}>
        No saved route/cost plan for this load yet -- full cost detail appears once one exists.
      </div>
    );
  }
  const fuelCaption = l.total_fuel_tax
    ? `Includes $${Math.round(l.total_fuel_tax).toLocaleString()} state fuel tax${
        l.fuel_tax_by_state && Object.keys(l.fuel_tax_by_state).length
          ? ` (${Object.entries(l.fuel_tax_by_state).map(([st, amt]) => `${st} $${Math.round(amt)}`).join(", ")})`
          : ""
      }`
    : null;
  return (
    <div style={styles.detailGrid}>
      <div style={styles.detailCol}>
        <div style={styles.detailColHead}>Cost breakdown</div>
        <CostLine icon={Fuel} label="Fuel" value={fmtMoney(l.fuel_cost)} caption={fuelCaption} />
        <CostLine icon={User} label="Driver pay" value={fmtMoney(l.driver_cost)} />
        <CostLine icon={RouteIcon} label="Tolls (planned)" value={fmtMoney(l.toll_cost)} />
        {l.insurance_cost > 0 && <CostLine icon={Shield} label="Insurance" value={fmtMoney(l.insurance_cost)} />}
        {l.office_payroll_cost > 0 && <CostLine icon={Building2} label="Office/payroll" value={fmtMoney(l.office_payroll_cost)} />}
        {l.maintenance_cost > 0 && <CostLine icon={Wrench} label="Maintenance" value={fmtMoney(l.maintenance_cost)} />}
        {l.other_cost > 0 && <CostLine icon={Package} label="Equipment" value={fmtMoney(l.other_cost)} />}
        <div style={styles.detailDivider} />
        <CostLine icon={Receipt} label="Total cost" value={fmtMoney(l.total_cost)} />
        <CostLine icon={TrendingUp} label="Profit" value={fmtMoney(l.contribution_profit)} accent={l.contribution_profit > 0} warn={l.contribution_profit <= 0} />
        {l.wait_hours > 0 && (
          <CostLine icon={Clock} label="Wait cost (opportunity)" value={`${fmtMoney(l.wait_cost)} (${fmtHours(l.wait_hours)})`} />
        )}
      </div>
      <div style={styles.detailCol}>
        <div style={styles.detailColHead}>Trip</div>
        <CostLine icon={RouteIcon} label="Deadhead miles" value={fmtMiles(l.deadhead_miles)} />
        <CostLine icon={RouteIcon} label="Loaded miles" value={fmtMiles(l.loaded_miles)} />
        <CostLine icon={RouteIcon} label="Total miles" value={fmtMiles(l.distance_miles)} accent />
        <CostLine icon={Clock} label="Drive duration" value={fmtHours(l.duration_hours)} />
        <CostLine icon={CalendarCheck} label="Actual delivery" value={l.actual_delivery_at ? fmtDate(l.actual_delivery_at) : "Not yet delivered"} />
        <div style={styles.detailDivider} />
        <CostLine icon={TrendingUp} label="Profit / mile" value={l.profit_per_mile != null ? fmtRate(l.profit_per_mile) : "—"} />
        <CostLine icon={TrendingUp} label="Profit / hour" value={l.profit_per_hour != null ? fmtMoney(l.profit_per_hour) : "—"} />
        {l.route_type && <CostLine icon={Package} label="Route picked" value={l.route_type.replace(/_/g, " ")} />}
        <div style={styles.detailDivider} />
        <RouteFidelity l={l} />
      </div>
    </div>
  );
}

function LoadRow({ l, open, onToggle }) {
  return (
    <div style={{ ...styles.row, ...(open ? styles.rowOpen : {}) }}>
      <div style={styles.rowHead} onClick={onToggle}>
        <div style={styles.rowLane}>
          <div style={styles.rowCustomer}>
            {l.customer_name || `${l.origin_location || "Origin"} → ${l.destination_location || "destination"}`}
            {l.off_route_events > 0 && (
              <span style={styles.offRouteFlag} title={`Went off the planned route ${l.off_route_events} time${l.off_route_events === 1 ? "" : "s"}`}>
                <AlertTriangle size={11} /> Off route
              </span>
            )}
          </div>
          <div style={styles.rowRoute}>{l.origin || "?"} → {l.destination || "?"}</div>
        </div>
        <span style={{ ...styles.statusBadge, color: statusColor(l.status), background: `${statusColor(l.status)}1a`, borderColor: `${statusColor(l.status)}55` }}>
          {statusLabel(l.status)}
        </span>
        <div style={styles.rowCell}>
          <div style={styles.rowCellMain}>{l.dispatcher_name || "Unassigned"}</div>
          <div style={styles.rowCellSub}>{l.unit_number ? `#${l.unit_number}` : "No truck"}</div>
        </div>
        <div style={styles.rowCell}>
          <div style={styles.rowCellMain}>{fmtDate(l.pickup_datetime)}</div>
          <div style={styles.rowCellSub}>booked {fmtDate(l.created_at)}</div>
        </div>
        <div style={{ ...styles.rowCell, textAlign: "right" }}>
          <div style={styles.rowCellMain}>{fmtMoney(l.rate_offered)}</div>
          <div style={styles.rowCellSub}>{l.distance_miles != null ? fmtMiles(l.distance_miles) : "no route"}</div>
        </div>
        <div style={{ ...styles.rowCell, textAlign: "right", minWidth: 74 }}>
          <div style={{ ...styles.rowCellMain, ...(l.contribution_profit != null && l.contribution_profit <= 0 ? { color: "#e0745d" } : { color: "var(--brand)" }) }}>
            {l.contribution_profit != null ? fmtMoney(l.contribution_profit) : "—"}
          </div>
          <div style={styles.rowCellSub}>{l.profit_per_mile != null ? fmtRate(l.profit_per_mile) : ""}</div>
        </div>
        <div style={styles.chevronWrap}>{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</div>
      </div>
      {open && <LoadDetail l={l} />}
    </div>
  );
}

const PAGE_SIZES = [20, 50, 100];

export default function LoadsPage() {
  const [rawSearch, setRawSearch] = useState("");
  const search = useDebounced(rawSearch, 300);
  const [preset, setPreset] = useState("all");
  // Dispatchers land on their OWN loads by default (their user id is stored at
  // login); admins/super_admins oversee everyone so they default to "All".
  // Either way it's just a pre-selected filter and can be changed.
  const [dispatcherId, setDispatcherId] = useState(() => (isAdmin() ? "" : (localStorage.getItem("id") || "")));
  const [status, setStatus] = useState("");
  const [equipmentTypeId, setEquipmentTypeId] = useState("");
  const [groupBy, setGroupBy] = useState(""); // "" = flat list, "driver" = per-driver totals
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openDriver, setOpenDriver] = useState(null); // expanded driver group (group-by view)

  // Any real filter change invalidates the current page -- staying on
  // page 3 after narrowing the result set down to 1 page would just show
  // an empty page instead of the results the new filter actually matches.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, dispatcherId, status, equipmentTypeId, search, groupBy, pageSize]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (preset !== "all") params.set("created_preset", preset);
    if (dispatcherId) params.set("dispatcher_id", dispatcherId);
    if (status) params.set("status", status);
    if (equipmentTypeId) params.set("equipment_type_id", equipmentTypeId);
    if (search.trim()) params.set("search", search.trim());
    if (groupBy) params.set("group_by", groupBy);
    params.set("page", page);
    params.set("page_size", pageSize);

    axios
      .get(`${apiBaseUrl}/scripted/fleet/loads-detail?${params.toString()}`, authHeaders())
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || err.response?.data?.detail || "Couldn't load loads."))
      .finally(() => setLoading(false));
  }, [preset, dispatcherId, status, equipmentTypeId, search, groupBy, page, pageSize]);

  const loads = data?.loads || [];
  const isGrouped = data?.grouped_by === "driver";
  const driverGroups = data?.driver_groups || [];
  // NOTE: no profit stat in the summary strip. The only per-load "profit"
  // available here is route_options.contribution_profit, which is revenue
  // minus the PER-LOAD costs (fuel/driver/tolls/insurance/office/maintenance)
  // but does NOT include the fixed weekly equipment cost (~$1,500/truck/wk,
  // charged even to idle trucks). Summing it reads as net profit but is
  // really contribution-before-equipment -- e.g. a ~$239k "profit" week was
  // actually ~$17k net once equipment was charged. True net profit lives in
  // the Truck Economics report (its total_cost includes equipment), so the
  // Loads summary deliberately shows only loads + revenue to avoid a
  // misleading headline number. (See the fleet-admin "show profit to
  // dispatchers?" discussion for the per-row/per-driver profit columns.)

  const activeFilterCount = [preset !== "all", !!dispatcherId, !!status, !!equipmentTypeId, !!search.trim()].filter(Boolean).length;
  const totalPages = data?.total_pages || 1;

  function clearFilters() {
    setRawSearch("");
    setPreset("all");
    setDispatcherId("");
    setStatus("");
    setEquipmentTypeId("");
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Loads</h1>
          <div style={styles.subtitle}>Every load, from any dispatcher -- filter down to what you need.</div>
        </div>
        {!loading && !error && (
          <div style={styles.summaryStrip}>
            <div style={styles.summaryStat}>
              <div style={styles.summaryValue}>{data?.total_count ?? 0}</div>
              <div style={styles.summaryLabel}>loads</div>
            </div>
            <div style={styles.summaryStat}>
              <div style={styles.summaryValue}>{fmtMoney(data?.total_revenue)}</div>
              <div style={styles.summaryLabel}>revenue</div>
            </div>
          </div>
        )}
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <Search size={14} style={styles.searchIcon} />
          <input
            style={styles.searchInput}
            type="search"
            placeholder="Search customer, lane, or load #..."
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>

        <div style={styles.presetGroup}>
          {DATE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              style={{ ...styles.presetBtn, ...(preset === p.key ? styles.presetBtnActive : {}) }}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <select style={styles.select} value={dispatcherId} onChange={(e) => setDispatcherId(e.target.value)}>
          <option value="">All dispatchers</option>
          {(data?.dispatcher_options || []).map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>

        <select style={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(data?.status_options || []).map((s) => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>

        <select style={styles.select} value={equipmentTypeId} onChange={(e) => setEquipmentTypeId(e.target.value)}>
          <option value="">All equipment</option>
          {(data?.equipment_type_options || []).map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>

        <button
          type="button"
          style={{ ...styles.presetBtn, ...(groupBy === "driver" ? styles.presetBtnActive : {}), display: "inline-flex", alignItems: "center", gap: 6 }}
          onClick={() => setGroupBy((g) => (g === "driver" ? "" : "driver"))}
          title="Group loads by driver, with per-driver totals over the selected filters"
        >
          <Users size={13} /> {groupBy === "driver" ? "Grouped by driver" : "Group by driver"}
        </button>

        {activeFilterCount > 0 && (
          <button type="button" style={styles.clearBtn} onClick={clearFilters}>
            <X size={12} /> Clear ({activeFilterCount})
          </button>
        )}
      </div>

      {error && <div style={styles.emptyState}>{error}</div>}
      {!error && !loading && !isGrouped && loads.length === 0 && (
        <div style={styles.emptyState}>No loads match these filters.</div>
      )}
      {!error && !loading && isGrouped && driverGroups.length === 0 && (
        <div style={styles.emptyState}>No loads match these filters.</div>
      )}

      {!error && !isGrouped && loads.length > 0 && (
        <div style={styles.board}>
          <div style={styles.colHeadRow}>
            <div style={{ flex: 1 }}>Customer / lane</div>
            <div style={{ width: 96 }}>Status</div>
            <div style={{ width: 150 }}>Dispatcher / truck</div>
            <div style={{ width: 130 }}>Pickup</div>
            <div style={{ width: 100, textAlign: "right" }}>Rate</div>
            <div style={{ width: 90, textAlign: "right" }}>Profit</div>
            <div style={{ width: 22 }} />
          </div>
          {loads.map((l) => (
            <LoadRow key={l.load_id} l={l} open={openId === l.load_id} onToggle={() => setOpenId(openId === l.load_id ? null : l.load_id)} />
          ))}
        </div>
      )}

      {!error && isGrouped && driverGroups.length > 0 && (
        <div style={styles.board}>
          <div style={{ ...styles.colHeadRow, paddingLeft: 62 }}>
            <div style={{ flex: 1 }}>Driver</div>
            <div style={{ width: 56, textAlign: "right" }}>Loads</div>
            <div style={{ width: 96, textAlign: "right" }}>Booked</div>
            <div style={{ width: 96, textAlign: "right" }}>Profit</div>
            <div style={{ width: 68, textAlign: "right" }}>$/mi</div>
            <div style={{ width: 80, textAlign: "right" }}>Miles</div>
            <div style={{ width: 80, textAlign: "right" }}>Tolls</div>
            <div style={{ width: 80, textAlign: "right" }}>Fuel</div>
            <div style={styles.chevronWrap} />
          </div>
          {driverGroups.map((g) => {
            const gkey = g.driver_id || "unassigned";
            const isOpen = openDriver === gkey;
            const profitPos = (g.total_profit || 0) >= 0;
            return (
              <div key={gkey} style={{ ...styles.row, ...(isOpen ? styles.rowOpen : {}) }}>
                <div style={styles.rowHead} onClick={() => setOpenDriver(isOpen ? null : gkey)}>
                  <div style={{ width: 34, height: 34, borderRadius: 999, flexShrink: 0, background: avatarColor(g.driver_name), color: "#fff", fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {initials(g.driver_name)}
                  </div>
                  <div style={styles.rowLane}>
                    <div style={styles.rowCustomer}>{g.driver_name}</div>
                    <div style={styles.rowRoute}>{g.load_count} load{g.load_count === 1 ? "" : "s"}{g.unit_number ? ` · Truck #${g.unit_number}` : ""}</div>
                  </div>
                  <div style={{ ...styles.rowCellMain, width: 56, textAlign: "right" }}>{g.load_count}</div>
                  <div style={{ ...styles.rowCellMain, width: 96, textAlign: "right" }}>{fmtMoney(g.total_revenue)}</div>
                  <div style={{ ...styles.rowCellMain, width: 96, textAlign: "right", color: profitPos ? "var(--brand)" : "#e0745d" }}>{fmtMoney(g.total_profit)}</div>
                  <div style={{ ...styles.rowCellMain, width: 68, textAlign: "right", fontWeight: 600, color: "var(--muted)" }}>{g.profit_per_mile != null ? fmtRate(g.profit_per_mile) : "—"}</div>
                  <div style={{ ...styles.rowCellMain, width: 80, textAlign: "right" }}>{Math.round(g.total_miles || 0).toLocaleString()}</div>
                  <div style={{ ...styles.rowCellMain, width: 80, textAlign: "right", fontWeight: 600 }}>{fmtMoney(g.total_toll)}</div>
                  <div style={{ ...styles.rowCellMain, width: 80, textAlign: "right", fontWeight: 600 }}>{fmtMoney(g.total_fuel)}</div>
                  <div style={styles.chevronWrap}>{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
                </div>
                {isOpen && (
                  <div style={{ borderTop: "1px solid var(--line)", background: "var(--bg)" }}>
                    {(g.loads || []).length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px 8px 62px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--muted)", borderBottom: "1px solid var(--line)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>Customer / lane</div>
                        <div style={{ width: 92 }}>Status</div>
                        <div style={{ width: 132, textAlign: "right" }}>Pickup → Delivery</div>
                        <div style={{ width: 88, textAlign: "right" }}>Booked</div>
                        <div style={{ width: 88, textAlign: "right" }}>Profit</div>
                        <div style={{ width: 62, textAlign: "right" }}>$/mi</div>
                        <div style={{ width: 70, textAlign: "right" }}>Miles</div>
                      </div>
                    )}
                    {(g.loads || []).length === 0 && <div style={styles.detailEmpty}>No loads.</div>}
                    {(g.loads || []).map((l) => (
                      <div key={l.load_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px 10px 62px", borderBottom: "1px solid var(--line)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>{l.customer_name || "Unknown"}</div>
                          <div style={styles.rowRoute}>{l.origin || "—"} → {l.destination || "—"}</div>
                        </div>
                        <span style={{ ...styles.statusBadge, width: 92, color: statusColor(l.status), background: `${statusColor(l.status)}1a`, borderColor: `${statusColor(l.status)}55` }}>{statusLabel(l.status)}</span>
                        <div style={{ width: 132, textAlign: "right", fontSize: 11, color: "var(--muted)", fontVariantNumeric: "tabular-nums" }}>{(l.pickup_datetime || "").slice(0, 10) || "—"} → {(l.delivery_datetime || "").slice(0, 10) || "—"}</div>
                        <div style={{ ...styles.rowCellMain, width: 88, textAlign: "right" }}>{fmtMoney(l.rate_offered)}</div>
                        <div style={{ ...styles.rowCellMain, width: 88, textAlign: "right", color: l.contribution_profit == null ? "var(--muted)" : (l.contribution_profit < 0 ? "#e0745d" : "var(--brand)") }}>{l.contribution_profit != null ? fmtMoney(l.contribution_profit) : "—"}</div>
                        <div style={{ width: 62, textAlign: "right", fontSize: 11, color: "var(--muted)", fontVariantNumeric: "tabular-nums" }}>{l.profit_per_mile != null ? fmtRate(l.profit_per_mile) : "—"}</div>
                        <div style={{ ...styles.rowCellMain, width: 70, textAlign: "right" }}>{l.distance_miles != null ? Math.round(l.distance_miles).toLocaleString() : "—"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!error && !isGrouped && (data?.total_count || 0) > 0 && (
        <div style={styles.pagination}>
          <div style={styles.pageSizeGroup}>
            <span style={styles.pageSizeLabel}>Rows per page</span>
            {PAGE_SIZES.map((n) => (
              <button
                key={n}
                type="button"
                style={{ ...styles.pageSizeBtn, ...(pageSize === n ? styles.pageSizeBtnActive : {}) }}
                onClick={() => setPageSize(n)}
              >
                {n}
              </button>
            ))}
          </div>
          <div style={styles.pageNav}>
            <span style={styles.pageNavLabel}>
              {(data.total_count === 0) ? "0 of 0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, data.total_count)} of ${data.total_count}`}
            </span>
            <button
              type="button"
              style={{ ...styles.pageNavBtn, ...(page <= 1 ? styles.pageNavBtnDisabled : {}) }}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={15} />
            </button>
            <span style={styles.pageNavLabel}>Page {page} of {totalPages}</span>
            <button
              type="button"
              style={{ ...styles.pageNavBtn, ...(page >= totalPages ? styles.pageNavBtnDisabled : {}) }}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "24px 28px 40px", maxWidth: 1120, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 18 },
  title: { fontSize: 26, fontWeight: 800, color: "var(--ink)", margin: 0, letterSpacing: -0.3 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  summaryStrip: { display: "flex", gap: 20 },
  summaryStat: { textAlign: "right" },
  summaryValue: { fontSize: 17, fontWeight: 800, color: "var(--ink)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" },
  summaryLabel: { fontSize: 10, color: "var(--muted)", marginTop: 2, textTransform: "uppercase", letterSpacing: 0.3 },

  filterBar: {
    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
    background: "var(--panel, #fff)", border: "1px solid var(--line)", borderRadius: 12,
    padding: 10, marginBottom: 18,
  },
  searchWrap: { position: "relative", flex: "1 1 220px", minWidth: 180 },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" },
  searchInput: {
    width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px 8px 30px",
    borderRadius: 8, border: "1px solid var(--line)", background: "var(--bg)", color: "var(--ink)",
  },
  presetGroup: { display: "flex", gap: 4, background: "var(--bg)", borderRadius: 8, padding: 3 },
  presetBtn: {
    fontSize: 11.5, fontWeight: 600, color: "var(--muted)", background: "transparent", border: "none",
    borderRadius: 6, padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap",
  },
  presetBtnActive: { background: "var(--panel, #fff)", color: "var(--brand)", boxShadow: "var(--shadow-xs, 0 1px 2px rgba(9,20,19,0.08))" },
  select: {
    fontSize: 12, padding: "7px 8px", borderRadius: 8, border: "1px solid var(--line)",
    background: "var(--bg)", color: "var(--ink)", maxWidth: 160,
  },
  clearBtn: {
    display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600,
    color: "var(--muted)", background: "transparent", border: "1px solid var(--line)",
    borderRadius: 8, padding: "6px 10px", cursor: "pointer",
  },

  emptyState: {
    padding: "28px 20px", textAlign: "center", color: "var(--muted)", fontSize: 13,
    background: "var(--panel, #fff)", border: "1px dashed var(--line)", borderRadius: 12,
  },

  board: { display: "flex", flexDirection: "column", gap: 8 },
  colHeadRow: {
    display: "flex", alignItems: "center", gap: 12, padding: "0 16px",
    fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4,
  },
  row: {
    background: "var(--panel, #fff)", border: "1px solid var(--line)", borderRadius: 12,
    overflow: "hidden", transition: "box-shadow 0.15s",
  },
  rowOpen: { boxShadow: "var(--shadow-sm, 0 1px 3px rgba(9,20,19,0.1))" },
  rowHead: { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" },
  rowLane: { flex: 1, minWidth: 0 },
  rowCustomer: { fontSize: 13, fontWeight: 700, color: "var(--ink)", display: "inline-flex", alignItems: "center", gap: 8 },
  offRouteFlag: {
    display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, fontWeight: 700,
    color: "#e0745d", background: "rgba(224,116,93,0.1)", border: "1px solid rgba(224,116,93,0.3)",
    borderRadius: 999, padding: "2px 7px", textTransform: "uppercase", letterSpacing: 0.3,
  },
  rowRoute: { fontSize: 11.5, color: "var(--muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  statusBadge: {
    width: 96, flexShrink: 0, fontSize: 10.5, fontWeight: 700, textAlign: "center",
    borderRadius: 999, padding: "4px 8px", border: "1px solid", textTransform: "capitalize",
  },
  rowCell: { width: 150, flexShrink: 0 },
  rowCellMain: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },
  rowCellSub: { fontSize: 10.5, color: "var(--muted)", marginTop: 1 },
  chevronWrap: { width: 22, color: "var(--muted)", flexShrink: 0, display: "flex", justifyContent: "flex-end" },

  detailEmpty: { padding: "4px 16px 16px", fontSize: 12, color: "var(--muted)" },
  detailGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24,
    padding: "6px 16px 18px", borderTop: "1px solid var(--line)", marginTop: 2,
  },
  detailCol: { display: "flex", flexDirection: "column", gap: 2, paddingTop: 12 },
  detailColHead: { fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 },
  detailDivider: { borderTop: "1px dashed var(--line)", margin: "4px 0" },
  costLine: { display: "flex", alignItems: "center", gap: 8, padding: "4px 0" },
  costLineIcon: { color: "var(--muted)", flexShrink: 0, display: "flex" },
  costLineLabel: { fontSize: 12, color: "var(--ink-2, var(--ink))" },
  costLineCaption: { fontSize: 10.5, color: "var(--muted)", marginTop: 1 },
  costLineValue: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums", flexShrink: 0 },

  pagination: {
    display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
    marginTop: 18, padding: "10px 4px",
  },
  pageSizeGroup: { display: "flex", alignItems: "center", gap: 6 },
  pageSizeLabel: { fontSize: 11.5, color: "var(--muted)", marginRight: 2 },
  pageSizeBtn: {
    fontSize: 11.5, fontWeight: 700, color: "var(--muted)", background: "var(--panel, #fff)",
    border: "1px solid var(--line)", borderRadius: 7, padding: "5px 10px", cursor: "pointer", minWidth: 32,
  },
  pageSizeBtnActive: { color: "#fff", background: "var(--brand)", borderColor: "var(--brand)" },
  pageNav: { display: "flex", alignItems: "center", gap: 10 },
  pageNavLabel: { fontSize: 11.5, color: "var(--muted)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  pageNavBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26,
    border: "1px solid var(--line)", borderRadius: 7, background: "var(--panel, #fff)",
    color: "var(--ink)", cursor: "pointer", padding: 0,
  },
  pageNavBtnDisabled: { opacity: 0.35, cursor: "default" },
};
